import React, { FC, useEffect, useState } from 'react';
import NextImage from 'src/shared-components/NextImage/NextImage';
import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { containerWithBackgroundImageStyle } from 'src/styles/shared';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import { useRouter } from 'next/router';
import useSSRMultyLevelCategoriesStore from 'src/hooks/useSSRMultyLevelCategoriesStore';
import useHostStore from 'src/hooks/useHostStore';
import {
  generateFirstLevelCategoryPageLink,
  supportPhoneNum,
  supportPhoneNumLink
} from '../../utils/links';
import Constraints from '../Constraints/Constraints';
import trns from './trns';
import NavigationLink from '../NavigationLink/NavigationLink';
import SocialMediaBots from '../Header/MobileMenu/SocialMediaBots';
import Subscription from './Subscription';
import LangSwitcher from './LangSwitcher';

const helpPath = 'shared/help.svg';
const logoImage = 'shared/logo-footer.svg';

interface Props {
  letters?: { translitName: string; name: string }[];
}

const Footer: FC<Props> = ({ letters }: Props) => {
  const { multyLevelCategoriesList } = useSSRMultyLevelCategoriesStore();
  const { hostURL } = useHostStore();
  const { trackGtagEvent } = useGTAG();
  const intl = useIntl();
  const router = useRouter();
  const [categories, setCategories] = useState([]);
  const listCategory = [
    {
      id: '619d6999f028457e35d75f79',
      category: intl.formatMessage(trns.category1)
    },
    {
      id: '619d63b15d70583f58246f99',
      category: intl.formatMessage(trns.category2)
    },
    {
      id: '619da7bdf028457e35d76748',
      category: intl.formatMessage(trns.category3)
    },
    {
      id: '619d7c2c175e911bff7c331c',
      category: intl.formatMessage(trns.category4)
    },
    {
      id: '619da74ff028457e35d76730',
      category: intl.formatMessage(trns.category5)
    },
    {
      id: '619dcef1175e911bff7c3ba3',
      category: intl.formatMessage(trns.category6)
    },
    {
      id: '619da315f028457e35d76652',
      category: intl.formatMessage(trns.category7)
    }
  ];

  const preparedCategories = () => {
    return multyLevelCategoriesList.reduce((acc, cur) => {
      listCategory.map((item) => {
        if (item.id === cur.id) {
          acc.push({
            ...cur,
            categoryName: item.category
          });
        }
        return '';
      });
      return [...acc];
    }, []);
  };

  useEffect(() => {
    if (multyLevelCategoriesList?.length) {
      setCategories(preparedCategories());
    }
  }, [
    // TODO: array as useEffect dependency should be replaced with primitive. Or deep use effect utilized
    multyLevelCategoriesList
  ]);

  return (
    <footer className="hidden m:flex m:w-full">
      <div
        style={containerWithBackgroundImageStyle}
        className="relative flex flex-col w-full text-mako fs-caption-reg"
      >
        {!!letters?.length && (
          <div className="bg-astronautBlue h-[72px] flex justify-between items-center lm:px-5 xl:px-0">
            {letters?.map((letterItem, index) => {
              return (
                <NavigationLink
                  key={index}
                  href={{ pathname: `/letter/${letterItem.translitName}` }}
                >
                  <span
                    className={cn(
                      'cursor-pointer fs-body-lg-reg text-white pb-[3px] px-[2px]'
                    )}
                  >
                    {letterItem.name}{' '}
                  </span>
                </NavigationLink>
              );
            })}
          </div>
        )}

        <div className={cn('flex w-full bg-wildSand', 'lm:px-5', 'xl:px-0')}>
          <Constraints className="flex flex-col m:flex-row m:w-full m:justify-between m:pt-14 m:pb-6">
            {/* First column */}
            <div
              className={cn(
                'flex flex-col w-full pr-5 pl-5',
                'm:w-6/12',
                'lm:pl-0'
              )}
            >
              <NavigationLink
                href={hostURL}
                onClick={() => {
                  trackGtagEvent(`${GTAGPrefix.footer}_logo`);
                }}
              >
                <div>
                  <NextImage
                    alt="Logo"
                    src={logoImage}
                    layout="fixed"
                    width={64}
                    height={24}
                  />
                </div>
              </NavigationLink>
              <p className="pt-4 fs-caption-reg pb-6 leading-5">
                <FormattedMessage {...trns.serviceDescription} />
              </p>
              <p className="pt-6 fs-caption-reg text-astronautBlue pb-6">
                <FormattedMessage {...trns.hologram} />
              </p>
            </div>
            {/* Second column */}
            <div
              className={cn(
                'flex flex-col px-5 w-full mb-5',
                'sm:px-0 sm:w-9/12'
              )}
            >
              <p className="pb-4 fs-body-reg text-astronautBlue uppercase">
                <FormattedMessage {...trns.category} />{' '}
              </p>
              <div className="flex flex-wrap h-36 justify-start">
                {categories.map((category) => {
                  return (
                    <div key={category.id} className="flex self-end w-2/4">
                      <NavigationLink
                        href={generateFirstLevelCategoryPageLink({
                          category
                        })}
                        onClick={() => {
                          trackGtagEvent(
                            `${GTAGPrefix.footer}_subcategory`,
                            category.categoryName,
                            'subcategory_name'
                          );
                        }}
                      >
                        <p className="fs-caption-reg cursor-pointer">
                          {category.categoryName}
                        </p>
                      </NavigationLink>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Third column */}
            <div
              className={cn(
                'flex flex-col px-5 mr-5 w-full',
                'sm:px-0 sm:w-1/4'
              )}
            >
              <p className="pb-4 fs-body-reg text-astronautBlue uppercase">
                <FormattedMessage {...trns.information} />{' '}
              </p>
              <NavigationLink
                href="/about_us"
                onClick={() => {
                  trackGtagEvent(`${GTAGPrefix.footer}_aboutus`);
                }}
              >
                <p className="pt-4 fs-caption-reg cursor-pointer">
                  <FormattedMessage {...trns.aboutUs} />{' '}
                </p>
              </NavigationLink>
              <NavigationLink
                href="/blog"
                onClick={() => {
                  trackGtagEvent(`${GTAGPrefix.footer}_blog`);
                }}
              >
                <p className="pt-4 fs-caption-reg cursor-pointer">
                  <FormattedMessage {...trns.tips} />{' '}
                </p>
              </NavigationLink>
              <NavigationLink
                href="/privacy_policy"
                onClick={() => {
                  trackGtagEvent(`${GTAGPrefix.footer}_conditions`);
                }}
              >
                <p className="pt-4 fs-caption-reg cursor-pointer">
                  <FormattedMessage {...trns.privacyPolicy} />{' '}
                </p>
              </NavigationLink>
              <NavigationLink
                href="/help"
                onClick={() => {
                  trackGtagEvent(`${GTAGPrefix.footer}_help`);
                }}
              >
                <p className="pt-4 fs-caption-reg cursor-pointer">
                  <FormattedMessage {...trns.help} />{' '}
                </p>
              </NavigationLink>
              <NavigationLink
                href="/partners/"
                onClick={() => {
                  trackGtagEvent(`${GTAGPrefix.footer}_for_partners`);
                }}
              >
                <p className="pt-4 fs-caption-reg cursor-pointer">
                  <FormattedMessage {...trns.partners} />{' '}
                </p>
              </NavigationLink>
            </div>
            {/* Fourth column */}
            <div
              className={cn(
                'flex flex-col px-5 mt-6',
                'lm:px-0 lm:mt-0 relative w-7/12'
              )}
            >
              <div className="pb-12">
                <div className="flex items-center pb-8">
                  <NextImage
                    alt={'Help'}
                    src={helpPath}
                    layout="fixed"
                    width={29}
                    height={28}
                  />
                  <button
                    className="px-5 fs-body-lg-reg underline text-astronautBlue whitespace-nowrap"
                    onClick={() => {
                      router.push(supportPhoneNumLink);
                      trackGtagEvent(`${GTAGPrefix.footer}_support_phone`);
                    }}
                  >
                    {supportPhoneNum}
                  </button>
                  <SocialMediaBots gtagPrefix={GTAGPrefix.footer} />
                </div>
                <div className="fs-caption-reg pb-8 leading-5">
                  <FormattedMessage {...trns.messageAboutBonus} />{' '}
                </div>
                <Subscription gtagPrefix={GTAGPrefix.footer} />
              </div>
              <div className="flex items-center justify-end">
                <LangSwitcher gtagPrefix={GTAGPrefix.footer} />
              </div>
            </div>
          </Constraints>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
